// Getting in (F7, F8). Supabase sign-up stays closed; the API makes the account (`POST /v1/signup`). With the server's
// `signup: "code"` that takes an access code or an invitation; with `"open"` (F8) anyone can sign up, a Cloudflare
// Turnstile token goes along, and the address is confirmed by mail before the first sign-in (`confirm`). Everything
// here is pure or takes its network pieces as arguments, so the flow and its sentences can be tested.
import { BRAND } from "./brand.ts";

/** What `POST /v1/signup` answers. */
export interface SignupResult {
  account: "created" | "exists";
  starter_credits: number;
  /** true: the account is made but the address must be confirmed first (a link was mailed); no password sign-in yet */
  confirm?: boolean;
}

/** Who may sign up, as GET /pricing says (`signup`). */
export type SignupMode = "open" | "code";

/** A refused sign-up: the HTTP status and the API's error code. */
export class SignupError extends Error {
  status: number;
  code: string;
  /** what the API said besides the message (`terms_required`: the terms version and address in force) */
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "SignupError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 72;
export const PASSWORD_HINT = `Use at least ${PASSWORD_MIN} characters for the password.`;

/** Why a password cannot be used, or null when it can (the server holds the same bounds). */
export function passwordProblem(password: string): string | null {
  if (password.length < PASSWORD_MIN) return PASSWORD_HINT;
  if (password.length > PASSWORD_MAX) return `Use at most ${PASSWORD_MAX} characters for the password.`;
  return null;
}

/** The refusals of `POST /v1/signup` in plain sentences. */
export function signupErrorText(
  code: string | undefined,
  fallback = "We could not create your account.",
): string {
  switch (code) {
    case "access_code_required":
      return `${BRAND.name} is invite-only for now. Enter the access code from your invitation.`;
    case "access_code_unknown":
      return "We don't recognise that access code. Check it and try again.";
    case "access_code_expired":
      return "That access code has expired. Ask whoever sent it for a new one.";
    case "access_code_used":
      return "That access code has already been used. Ask whoever sent it for a new one.";
    case "turnstile_failed":
    case "turnstile_required":
    case "captcha_failed":
      return "We could not check that you are a person. Try again.";
    case "terms_required":
      return "Please agree to the current Terms of Service and Privacy Policy to create your account.";
    case "signup_unavailable":
      return "Creating accounts is not switched on for this studio. Ask your admin to add you.";
    case "auth_provider_error":
      return "We could not create your account just now. Try again in a minute.";
    case "validation_error":
      return /password/i.test(fallback) ? PASSWORD_HINT : fallback;
    default:
      return fallback;
  }
}

/** Supabase says no to a sign-in link for an address that has no account (sign-ups are closed). */
export function isSignupsClosed(e: unknown): boolean {
  if (!e || typeof e !== "object") return false;
  const { code, message } = e as { code?: unknown; message?: unknown };
  return code === "otp_disabled" || (typeof message === "string" && /signups? not allowed/i.test(message));
}

/** The sentence under an error: the API's own words for a sign-up refusal, else the error's message. */
export function signInErrorText(e: unknown): string {
  if (e instanceof SignupError) return signupErrorText(e.code, e.message);
  const { code, message } = (e && typeof e === "object" ? e : {}) as { code?: unknown; message?: unknown };
  if (
    code === "invalid_credentials" ||
    (typeof message === "string" && /invalid login credentials/i.test(message))
  )
    return "That email and password don't match. Try again, or have a sign-in link emailed to you.";
  if (code === "email_not_confirmed")
    return "This address is not confirmed yet. Have a sign-in link emailed to you.";
  return e instanceof Error ? e.message : String(e);
}

/** "500 credits · €25.00 to start": whole credits, thousands separated, euros with cents. */
export const starterLine = (credits: number, creditEur = 0.05): string =>
  `${creditsAndEuros(credits, creditEur)} to start`;

/** "500 credits · €25.00": a credit amount with its euros, from the server's credit price. */
export function creditsAndEuros(credits: number, creditEur = 0.05): string {
  const eur = Math.round(credits * creditEur * 100) / 100;
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(credits)} credits · €${eur.toFixed(2)}`;
}

/** The first workspace's welcome: what the access code put on it. */
export const studioStartLine = (credits: number, creditEur = 0.05): string =>
  `Your studio starts with ${creditsAndEuros(credits, creditEur)}`;

export interface CreateAccountDeps {
  signUp: (
    email: string,
    code: string | undefined,
    inviteToken: string | undefined,
    password: string,
    turnstileToken?: string,
    /** the terms version the person agreed to (the server refuses a sign-up without it) */
    acceptTerms?: string,
  ) => Promise<SignupResult>;
  signIn: (email: string, password: string) => Promise<void>;
  /** the account exists now and these credits wait for its first workspace (called before signing in) */
  onCreated?: (starterCredits: number) => void;
}

export type CreateOutcome =
  /** made and signed in; the app takes over */
  | { kind: "signed_in"; starter: number }
  /** the address already had an account (its password was not changed): sign in instead */
  | { kind: "exists" }
  /** made, but the address must be confirmed: a link was mailed to `email` and it signs the person in */
  | { kind: "check_email"; email: string };

export const EXISTS_NOTICE = "You already have an account: sign in.";

/** The "Check your email" state after a sign-up that needs the address confirmed. */
export const CHECK_EMAIL_TITLE = "Check your email";
export const checkEmailText = (email: string): string =>
  `We sent a link to ${email}. Open it on this device: it confirms your address and signs you in.`;

/**
 * Create an account: with the access code when one is typed, else with the pending invitation's token (a code is
 * needed when sign-up is by code, optional when it is open). A new account signs in straight away with the password it was made
 * with, unless the server wants the address confirmed first: then nobody is signed in and the mail does it.
 */
export async function createAccount(
  deps: CreateAccountDeps,
  input: {
    email: string;
    password: string;
    code?: string;
    inviteToken?: string | null;
    mode?: SignupMode;
    turnstileToken?: string | null;
    /** the terms version in force that the person agreed to */
    acceptTerms?: string | null;
  },
): Promise<CreateOutcome> {
  const email = input.email.trim();
  const problem = passwordProblem(input.password);
  if (problem) throw new Error(problem);
  const code = input.code?.trim() || undefined;
  const token = code ? undefined : input.inviteToken || undefined;
  if (!token && !code && (input.mode ?? "code") === "code")
    throw new SignupError(403, "access_code_required", "an access code is needed");
  // trailing arguments only when there are any: (…, turnstile) or (…, turnstile or undefined, acceptTerms)
  const extra: [string?, string?] = input.acceptTerms
    ? [input.turnstileToken || undefined, input.acceptTerms]
    : input.turnstileToken
      ? [input.turnstileToken]
      : [];
  const r = await deps.signUp(email, code, token, input.password, ...extra);
  if (r.account === "exists") return { kind: "exists" };
  if (r.confirm) return { kind: "check_email", email };
  deps.onCreated?.(r.starter_credits);
  await deps.signIn(email, input.password);
  return { kind: "signed_in", starter: r.starter_credits };
}
